
import set from "lodash-es/set";
import filter from "lodash-es/filter";
import cloneDeep from "lodash-es/cloneDeep";
import get from "lodash-es/get";
import find from "lodash-es/find";
import map from "lodash-es/map";
import some from "lodash-es/some";
import every from "lodash-es/every";
import "es6-promise";

import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";
import { FileListItem } from "../file-tree-util";
import { Sync } from "vuex-pathify";

import RemoteFilesSelect from "./RemoteSelect/RemoteFilesSelect.vue";
import RemoteFileSelectAboutBox from "./RemoteSelect/RemoteFileSelectAboutBox.vue";

import BannerNotice from "./BannerNotice.vue";

import { ReferenceGenome } from "../types";
import { isValidUrl, sortReferenceGenomesByPreference } from "../util";

@Component({
  components: { RemoteFilesSelect, BannerNotice },
  filters: {}
})
export default class SelectGenome extends Vue {
  ensemblRelease: string = "114";
  public ensemblReleaseUrl: string = `http://ftp.ensembl.org/pub/release-${this.ensemblRelease}`;

  @Sync("pipelineParams@user_genome.fasta_url")
  public custom_genome_fasta_url: string;

  @Sync("pipelineParams@user_genome.annotation_url")
  public custom_genome_annotation_url: string;

  @Sync("pipelineParams@genome")
  public reference_genome: string;

  public custom_genome_fileselect_url: string = "";

  @Prop({ default: () => null, type: Array })
  public genomes: Array<ReferenceGenome> | null;

  @Prop({ default: null, type: String })
  public initialGenomeId: string | null;

  @Sync("use_custom_genome")
  public use_custom_genome: boolean;

  @Watch("use_custom_genome")
  onCustomGenomeToggle(newVal: boolean, oldVal: boolean) {
    this.update_genome_values_valid();
  }

  // This variable is used in addition to the toggle()/open()/close()
  // methods on the mdSidenav component, since toggling closed doesn't
  // properly hide the component (eg if we scroll to the right in an MdStepper)
  // TODO: This isn't ideal, since we skip the open/close animation. Patching
  // mdSidenav with a v-show tied to the end of the open/close animation
  // would be better.
  public show_fileselect_slideout = false;

  public fasta_url_valid = true;
  public annotation_url_valid = true;

  ref_sequence_extensions = [
    ".fasta.gz",
    ".fasta",
    ".fa",
    ".fa.gz",
    ".fna",
    ".fna.gz"
  ];
  ref_annotation_extensions = [
    ".gtf.gz",
    ".gff.gz",
    ".gff3.gz",
    ".gtf",
    ".gff",
    ".gff3"
  ];

  created() {
    if (this.initialGenomeId) {
      this.reference_genome = this.initialGenomeId;
    }
  }

  get effectiveGenomes(): Array<ReferenceGenome> {
    const fromProp = this.genomes;
    if (fromProp && fromProp.length > 0) {
      return fromProp;
    }
    const fromStore = this.$store.state.availableGenomes as
      | ReferenceGenome[]
      | undefined;
    if (fromStore && fromStore.length > 0) {
      return fromStore;
    }
    return [];
  }

  get selected_genome_organism(): string {
    return (
      this.get_organism_from_genome_id(
        this.$store.state.pipelineParams.genome
      ) || "Homo sapiens"
    );
  }

  set selected_genome_organism(organism: string) {
    const id =
      this.get_first_genome_id_for_organism(organism) ||
      this.effectiveGenomes[0].id;
    this.$store.set("pipelineParams@genome", id);
  }

  get genome_organism_list(): string[] {
    let organisms = new Set<string>();
    for (let g of this.effectiveGenomes) {
      organisms.add(g.organism);
    }
    return Array.from(organisms.values());
  }

  genomes_for_organism(organism: string): ReferenceGenome[] {
    const organism_genomes: ReferenceGenome[] = [];
    for (let g of this.effectiveGenomes) {
      if (g.organism === organism) {
        organism_genomes.push(g);
      }
    }
    return sortReferenceGenomesByPreference(organism_genomes);
  }

  get_organism_from_genome_id(genome_id: string): string | undefined {
    return get(find(this.effectiveGenomes, { id: genome_id }), "organism");
  }

  get_first_genome_id_for_organism(organism: string): string | undefined {
    const ordered = this.genomes_for_organism(organism);
    return ordered.length > 0 ? ordered[0].id : undefined;
  }

  get_genome_description(reference: ReferenceGenome): string {
    const [org, centre, build] = reference.id.split("/");
    // return `${build} [${centre}] (${reference.organism})`;
    let desc = `${build} [${centre}]`;
    if (reference.recommended) {
      desc = `${desc} (recommended)`;
    }
    return desc;
  }

  update_genome_values_valid() {
    let valid = false;
    if (this.use_custom_genome) {
      valid =
        this.custom_genome_fasta_url !== "" &&
        this.custom_genome_annotation_url !== "" &&
        this.fasta_url_valid &&
        this.annotation_url_valid;
    } else {
      valid = this.$store.get("pipelineParams@genome") != null;
    }

    this.$store.set("genome_values_valid", valid);

    return valid;
  }

  onOrganismChange(e: any) {
    this.reference_genome = this.genomes_for_organism(
      this.selected_genome_organism
    )[0].id;
  }

  onFastaUrlChange(url: any) {
    this.fasta_url_valid =
      (url && isValidUrl(url, ["http", "https", "ftp"])) ||
      this.custom_genome_fasta_url === "";

    this.update_genome_values_valid();
  }

  onAnnotationUrlChange(url: any) {
    this.annotation_url_valid =
      (url && isValidUrl(url, ["http", "https", "ftp"])) ||
      this.custom_genome_annotation_url === "";

    this.update_genome_values_valid();
  }

  async addCustomGenomeFiles(addedFiles: FileListItem[]) {
    for (let f of addedFiles) {
      for (let ext of this.ref_sequence_extensions) {
        if (f.name.endsWith(ext)) {
          this.custom_genome_fasta_url = f.location;
          this.onFastaUrlChange(this.custom_genome_fasta_url);
        }
      }
      for (let ext of this.ref_annotation_extensions) {
        if (f.name.endsWith(ext)) {
          this.custom_genome_annotation_url = f.location;
          this.onAnnotationUrlChange(this.custom_genome_annotation_url);
        }
      }
    }

    this.toggleFileSelect();
    const el = document.getElementById("_top"); // this.$refs['custom-genome-section'] as Element;
    // el.scrollIntoView({behavior: "smooth", block: "start"});
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // el.scrollTo(0, 0);
  }

  toggleFileSelect(url?: string) {
    this.custom_genome_fileselect_url = url || "";
    this.toggleSlideout("fileSelectSlideout");
  }

  toggleSlideout(refName: string) {
    const sidenav = (this.$refs as any)[refName] as any;
    sidenav.toggle();
  }

  openSlideout(ref: string) {
    if (ref === "fileSelectSlideout") {
      this.show_fileselect_slideout = true;
    }
    // console.log('Opened: ' + ref);
  }

  closeSlideout(ref: string) {
    if (ref === "fileSelectSlideout") {
      this.show_fileselect_slideout = false;
    }
    // console.log('Closed: ' + ref);
  }
}
